import { useState } from "react";
import axios from "axios";
import { CreditCard, Lock, ArrowRight, Loader2, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [25, 50, 100, 250];

export default function Donate() {
  const { t } = useLang();
  const d = t.donate;
  const [interval, setInterval] = useState("once");
  const [preset, setPreset] = useState(50);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);

  const amount = preset ?? parseFloat(custom);
  const valid = Number.isFinite(amount) && amount >= 5 && amount <= 25000;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return toast.error(d.amountError);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/donations/checkout`, {
        amount,
        interval,
        donor_name: name || null,
        donor_email: email || null,
        anonymous,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || d.error);
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-[#0A0C10] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-amber-400";

  return (
    <form
      onSubmit={submit}
      data-testid="donate-card"
      className="relative overflow-hidden border border-amber-400/50 bg-[#0E1119] p-7 sm:p-10"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-amber-400/10 blur-3xl" />
      <div className="relative grid gap-10 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center border border-amber-400/60 text-amber-400">
              <CreditCard size={20} />
            </div>
            <span className="font-code text-[11px] uppercase tracking-[0.3em] text-amber-400">
              {d.badge}
            </span>
          </div>
          <h3 className="mt-6 font-display text-3xl font-bold uppercase text-white sm:text-4xl">
            {d.title}
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
            {d.desc}
          </p>

          <div
            data-testid="donate-interval-toggle"
            className="mt-8 grid grid-cols-2 border border-white/15"
          >
            {["once", "month"].map((k) => (
              <button
                type="button"
                key={k}
                data-testid={`donate-interval-${k}`}
                onClick={() => setInterval(k)}
                className={`px-4 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] transition-colors ${
                  interval === k
                    ? "bg-amber-400 text-[#0A0C10]"
                    : "text-slate-300 hover:text-white"
                }`}
              >
                {k === "once" ? d.once : d.monthly}
              </button>
            ))}
          </div>
          {interval === "month" && (
            <p className="mt-3 text-xs text-slate-500">{d.monthlyHint}</p>
          )}

          <p className="mt-8 font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">
            {d.amountLabel}
          </p>
          <div className="mt-3 grid grid-cols-4 gap-2">
            {PRESETS.map((p) => (
              <button
                type="button"
                key={p}
                data-testid={`donate-preset-${p}`}
                onClick={() => {
                  setPreset(p);
                  setCustom("");
                }}
                className={`border py-3 font-display text-lg font-bold transition-colors ${
                  preset === p
                    ? "border-amber-400 bg-amber-400/10 text-amber-400"
                    : "border-white/15 text-white hover:border-white/40"
                }`}
              >
                ${p}
              </button>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <span className="shrink-0 text-xs uppercase tracking-[0.2em] text-slate-500">
              {d.custom}
            </span>
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                $
              </span>
              <input
                data-testid="donate-custom-amount"
                type="number"
                min="5"
                max="25000"
                step="1"
                inputMode="decimal"
                value={custom}
                onChange={(e) => {
                  setCustom(e.target.value);
                  setPreset(null);
                }}
                onFocus={() => setPreset(null)}
                placeholder={d.customPlaceholder}
                className={`${inputCls} pl-8`}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-between border-t border-white/10 pt-8 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
          <div className="space-y-5">
            <label className="block">
              <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">
                {d.nameLabel}
              </span>
              <input
                data-testid="donate-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={anonymous}
                placeholder={d.namePlaceholder}
                maxLength={120}
                className={`${inputCls} mt-2 disabled:opacity-40`}
              />
            </label>
            <label className="block">
              <span className="font-code text-[11px] uppercase tracking-[0.3em] text-slate-400">
                {d.emailLabel}
              </span>
              <input
                data-testid="donate-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={d.emailPlaceholder}
                maxLength={160}
                className={`${inputCls} mt-2`}
              />
            </label>
            <label
              data-testid="donate-anonymous-toggle"
              className="flex cursor-pointer items-start gap-3 border border-white/10 p-4 transition-colors hover:border-white/25"
            >
              <input
                type="checkbox"
                checked={anonymous}
                onChange={(e) => setAnonymous(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amber-400"
              />
              <span>
                <span className="flex items-center gap-2 text-sm font-semibold text-white">
                  <EyeOff size={14} className="text-amber-400" />
                  {d.anonymous}
                </span>
                <span className="mt-1 block text-xs text-slate-500">
                  {d.anonymousHint}
                </span>
              </span>
            </label>
          </div>

          <div className="mt-8">
            <button
              type="submit"
              data-testid="donate-submit-button"
              disabled={loading || !valid}
              className="group flex w-full items-center justify-center gap-3 bg-amber-400 px-5 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {d.redirecting}
                </>
              ) : (
                <>
                  {interval === "month" ? d.ctaMonthly : d.cta}
                  {valid && (
                    <span className="font-code text-xs tracking-normal opacity-70">
                      · ${amount}{interval === "month" ? "/mo" : ""}
                    </span>
                  )}
                  <ArrowRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </>
              )}
            </button>
            <p className="mt-3 flex items-center justify-center gap-2 text-center text-[11px] text-slate-500">
              <Lock size={11} /> {d.secure}
            </p>
          </div>
        </div>
      </div>
    </form>
  );
}
